package org.teddapp.project.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddFilterChip
import org.teddapp.project.ui.TeddFilterRow
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge
import org.teddapp.project.ui.teddSurface

// ──────────────────────────────────────────────────────────────────────
// La charpente du back-office : la seconde barre latérale, rangée comme
// Objectifs (Site public, puis Système), l'en-tête de chaque rubrique, les
// filtres, et le tableau qui liste les contenus en fenêtre large.
// ──────────────────────────────────────────────────────────────────────

/** Ce que montre une rubrique : tout, les brouillons et retirés, ou ce qui est en ligne. */
internal enum class RubricFilter(val label: String) { All("Tous"), Drafts("Brouillons"), Online("En ligne") }

/** La seconde barre latérale, à gauche du contenu : une entrée par rubrique, avec son compte. */
/** Les rubriques du back-office : 248 dp par défaut, de 200 à 360. */
private val ADMIN_PANE = org.teddapp.project.ui.PaneSpec("admin.sidebar", default = TeddSize.Sidebar, min = 200.dp, max = 360.dp, collapseBelow = 150.dp)

@Composable
internal fun AdminSidebar(model: AdminModel) {
    val uri = LocalUriHandler.current
    // Le filet du bord est la poignée : on y règle la largeur de la colonne.
    org.teddapp.project.ui.TeddSidePane(
        spec = ADMIN_PANE,
        collapsedContent = { expand ->
            org.teddapp.project.ui.TeddPaneRail(
                expand = expand,
                title = "les rubriques",
                items = SiteRubric.entries.map { r -> org.teddapp.project.ui.PaneRailItem(r.name, r.label, r.icon, r == model.rubric) { model.openRubric(r) } },
            )
        },
    ) {
    Row(Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier.weight(1f).fillMaxHeight().verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            SidebarLabel("Site public")
            SiteRubric.entries.filter { !it.system }.forEach { SidebarEntry(model, it) }
            Spacer(Modifier.height(TeddSpace.S3))
            SidebarLabel("Système")
            SiteRubric.entries.filter { it.system }.forEach { SidebarEntry(model, it) }
            Spacer(Modifier.weight(1f))
            Spacer(Modifier.height(TeddSpace.S3))
            TeddButton(
                text = "Voir le site",
                onClick = { uri.openUri(model.siteUrl + "/fr/") },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                icon = "globe",
                modifier = Modifier.fillMaxWidth(),
            )
            Text(
                "Toi seul vois cet écran.",
                color = TeddTokens.InkMuted,
                style = TeddType.Caption,
                modifier = Modifier.padding(start = TeddSpace.S2, top = TeddSpace.S1),
            )
        }
    }
    }
}

@Composable
private fun SidebarLabel(label: String) {
    Text(
        text = label.uppercase(),
        modifier = Modifier.padding(start = TeddSpace.S1, top = TeddSpace.S1, bottom = TeddSpace.S1),
        color = TeddTokens.InkMuted,
        style = TeddType.Overline,
    )
}

@Composable
private fun SidebarEntry(model: AdminModel, r: SiteRubric) {
    TeddFilterRow(r.label, r == model.rubric, { model.openRubric(r) }, count = model.countOf(r)) {
        TeddIcon(name = r.icon, size = 18.dp, color = if (r == model.rubric) TeddTokens.Ink else TeddTokens.InkMuted)
    }
}

/** Sur téléphone, pas de barre latérale : les rubriques tiennent dans une rangée de puces. */
@Composable
internal fun RubricChips(model: AdminModel) {
    Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        SiteRubric.entries.forEach { r ->
            TeddFilterChip(label = r.label, selected = r == model.rubric, onClick = { model.openRubric(r) }, count = model.countOf(r))
        }
    }
}

/**
 * L'en-tête d'une rubrique : d'où elle vient (Site public, Système), son nom, une phrase
 * qui dit ce qu'elle tient, et ses gestes : voir la page sur le site, créer.
 */
@Composable
internal fun RubricHeader(model: AdminModel, compact: Boolean, actions: @Composable RowScope.() -> Unit = {}) {
    val r = model.rubric
    val uri = LocalUriHandler.current
    val path = r.sitePath
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Row(verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                Text(
                    (if (r.system) "Système · " else "Site public · ") + r.label,
                    color = TeddTokens.InkMuted,
                    style = TeddType.MonoSm,
                    maxLines = 1,
                )
                Text(r.label, color = TeddTokens.Ink, style = if (compact) TeddType.Title1 else TeddType.DisplayLg)
                if (!compact) Text(r.blurb, color = TeddTokens.InkMuted, style = TeddType.Body)
            }
            if (!compact) {
                if (path != null) TeddButton(
                    text = "Voir $path",
                    onClick = { uri.openUri(model.siteUrl + path) },
                    kind = TeddButtonKind.Secondary,
                    icon = "eye",
                )
                actions()
            }
        }
        if (compact) Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
        ) {
            if (path != null) TeddButton(
                text = "Voir sur le site",
                onClick = { uri.openUri(model.siteUrl + path) },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                icon = "eye",
            )
            actions()
        }
    }
}

/** Tous · Brouillons · En ligne, avec leur compte, et un champ qui filtre par titre. */
@Composable
internal fun FilterBar(model: AdminModel, items: List<org.teddapp.project.server.ServerPublication>, compact: Boolean) {
    Row(
        modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        TeddTabs(
            tabs = RubricFilter.entries.map { f ->
                TeddTab(f.name, f.label, count = items.count { f.keeps(it) }.takeIf { f != RubricFilter.All || it > 0 })
            },
            selected = model.filter.name,
            onSelect = { name -> RubricFilter.entries.firstOrNull { it.name == name }?.let { model.filter = it } },
        )
        if (!compact) Spacer(Modifier.weight(1f))
        SearchField(model.search, { model.search = it }, Modifier.width(if (compact) 200.dp else 260.dp))
    }
}

internal fun RubricFilter.keeps(p: org.teddapp.project.server.ServerPublication) = when (this) {
    RubricFilter.All -> true
    RubricFilter.Drafts -> !p.online
    RubricFilter.Online -> p.online
}

/** Un contenu passe le filtre et la recherche de la rubrique. */
internal fun AdminModel.shows(p: org.teddapp.project.server.ServerPublication): Boolean {
    if (!filter.keeps(p)) return false
    val q = search.trim()
    return q.isEmpty() || p.title.contains(q, ignoreCase = true) || p.slug.contains(q, ignoreCase = true)
}

@Composable
private fun SearchField(value: String, onValueChange: (String) -> Unit, modifier: Modifier = Modifier) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = modifier
            .height(34.dp)
            .clip(shape)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape)
            .padding(horizontal = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TeddIcon(name = "search", size = 13.dp, color = TeddTokens.InkMuted)
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            modifier = Modifier.weight(1f),
            singleLine = true,
            textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 13.sp),
            cursorBrush = SolidColor(TeddTokens.Ink),
            decorationBox = { inner ->
                if (value.isEmpty()) Text("Rechercher", color = TeddTokens.InkSubtle, style = TeddType.Body.copy(fontSize = 13.sp))
                inner()
            },
        )
    }
}

// ── Le tableau ─────────────────────────────────────────────────────────

/** Une colonne : son titre, et sa part de la largeur, ou une largeur fixe. */
internal class TableColumn(val label: String, val weight: Float = 1f, val width: Dp? = null)

/**
 * Un tableau posé sur une surface : une ligne d'en-têtes, puis une ligne par contenu,
 * séparées d'un filet. Chaque cellule est un composable, posé dans sa colonne.
 */
@Composable
internal fun AdminTable(columns: List<TableColumn>, rows: List<List<@Composable () -> Unit>>, modifier: Modifier = Modifier) {
    Column(modifier = modifier.fillMaxWidth().teddSurface(RoundedCornerShape(TeddTokens.RMd))) {
        Row(
            modifier = Modifier.fillMaxWidth().background(TeddTokens.SurfaceSunken).padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S2),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            columns.forEach { c -> Box(c.cellModifier(this)) { Text(c.label.uppercase(), color = TeddTokens.InkMuted, style = TeddType.Overline, maxLines = 1) } }
        }
        rows.forEachIndexed { index, cells ->
            Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S4, vertical = TeddSpace.S3),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
            ) {
                columns.forEachIndexed { i, c -> Box(c.cellModifier(this)) { cells.getOrNull(i)?.invoke() } }
            }
        }
    }
}

private fun TableColumn.cellModifier(scope: RowScope): Modifier = with(scope) {
    if (width != null) Modifier.width(width) else Modifier.weight(weight)
}

/** Une cellule de tableau : un titre, et dessous un détail en petit. */
@Composable
internal fun TitleCell(title: String, detail: String?, muted: Boolean = false, onClick: (() -> Unit)? = null) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        if (onClick != null) {
            TeddButton(text = title, onClick = onClick, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, modifier = Modifier.padding(start = 0.dp))
        } else {
            Text(title, color = if (muted) TeddTokens.InkMuted else TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 2, overflow = TextOverflow.Ellipsis)
        }
        if (!detail.isNullOrBlank()) Text(detail, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
    }
}

@Composable
internal fun MonoCell(text: String) = Text(text, color = TeddTokens.InkMuted, style = TeddType.MonoSm, maxLines = 1, overflow = TextOverflow.Ellipsis)

/** L'état d'une publication : en ligne, brouillon, retirée, exemple. */
@Composable
internal fun StateBadge(p: org.teddapp.project.server.ServerPublication) {
    if (p.example) TeddTag("Exemple")
    if (p.online) TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne")
    else TeddTag(if (p.isPage || p.example) "Brouillon" else "Retirée")
}

/** Le chemin d'un contenu dans une rubrique : Brouillon → En ligne, et ce que chaque pas veut dire. */
@Composable
internal fun FlowBanner() {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    val line = TeddTokens.LineStrong
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .drawBehind {
                drawRoundRect(
                    color = line,
                    cornerRadius = CornerRadius(12.dp.toPx()),
                    style = Stroke(width = 1.dp.toPx(), pathEffect = PathEffect.dashPathEffect(floatArrayOf(6.dp.toPx(), 5.dp.toPx()))),
                )
            }
            .clip(shape)
            .padding(horizontal = TeddSpace.S5, vertical = TeddSpace.S4)
            .horizontalScroll(rememberScrollState()),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        Text("LE FLUX", color = TeddTokens.InkMuted, style = TeddType.Overline)
        TeddTag("Brouillon")
        Text("tu écris, le site ne voit rien", color = TeddTokens.InkMuted, style = TeddType.Caption)
        TeddIcon(name = "arrow-right", size = 14.dp, color = TeddTokens.InkMuted)
        TeddVisibilityBadge(TeddVisibility.Public, label = "En ligne")
        Text("une langue à la fois ; le site se reconstruit en une minute", color = TeddTokens.InkMuted, style = TeddType.Caption)
        TeddIcon(name = "arrow-right", size = 14.dp, color = TeddTokens.InkMuted)
        TeddTag("Retirée")
        Text("la copie reste, tu peux la remettre en ligne", color = TeddTokens.InkMuted, style = TeddType.Caption)
    }
}
